import { createSelector } from 'reselect'
import GrooveCards from 'api/GrooveCards'
import { memoize } from 'util/memoization'

import {
  selectIsCreatingNewEmail,
  selectIsLoggingNewConversation,
} from 'selectors/page'
import * as locations from 'constants/context_locations'
import {
  selectCurrentCustomer,
  selectCurrentTicket,
  selectCurrentTicketId,
  selectTicketsById,
} from 'selectors/tickets'
import { selectReplyDraft, selectNoteDraft } from 'ducks/drafts'
// eslint-disable-next-line no-unused-vars
import { selectSelectedTicketsAuthors } from 'selectors/ticket_list'
import {
  selectSelectedTicketIds,
  selectIsTicketSelectionMode,
} from 'selectors/ticket_list/base'

import { camelize } from 'util/strings'
import { filter, mapObjectKeys, isEmpty as isEmptyObject } from 'util/objects'
import { getFirstOrEmptyFromDraftRecipient } from 'util/draft'

const byKeyDefault = {}

export const selectCardsByKey = state =>
  state.embeddableCards.byKey || byKeyDefault

export const selectCards = createSelector(selectCardsByKey, byKey => {
  return Object.values(byKey).map(card => ({
    ...card,
    api: GrooveCards.forCard(card.key),
    hasTab: !!card.tabTitle,
  }))
})

export const selectTicketSelectionCacheKey = createSelector(
  selectSelectedTicketIds,
  ticketIds => {
    return {
      ticketIds,
    }
  }
)

export const selectTicketCacheKey = createSelector(
  selectCurrentTicket,
  selectCurrentCustomer,
  (ticket, customer) => {
    if (!ticket || !ticket.id || !customer) return null
    return {
      ticketId: ticket.id,
      customerId: customer.id,
      customerEmail: customer.email,
      messageCount: ticket.message_count,
    }
  }
)

export const selectNewEmailCacheKey = createSelector(
  selectReplyDraft,
  draft => {
    const customer = getFirstOrEmptyFromDraftRecipient('to', draft)
    if (!draft || !customer || isEmptyObject(customer)) return null
    return {
      ticketId: draft.id, // not really used nor unique
      customerId: customer.id,
      customerEmail: customer.email,
    }
  }
)

export const selectNewNoteCacheKey = createSelector(selectNoteDraft, draft => {
  const customer = getFirstOrEmptyFromDraftRecipient('to', draft)
  if (!draft || !customer || isEmptyObject(customer)) return null
  return {
    ticketId: draft.id, // not really used nor unique
    customerId: customer.id,
    customerEmail: customer.email,
  }
})

// Single Ticket Context
export const selectTicketContext = createSelector(
  selectCurrentTicket,
  ticket => {
    if (!ticket || !ticket.id) return null
    const ticketWithoutActions = Object.assign({}, ticket)
    return {
      ticket: ticketWithoutActions,
    }
  }
)

// Bulk Ticket Selection Context
export const selectTicketSelectionContext = createSelector(
  selectSelectedTicketIds,
  selectTicketsById,
  // selectSelectedTicketsAuthors,
  (ticketIds, ticketsById, authors = []) => {
    const ticketWithoutActions = ticketIds.map(id => {
      const ticket = Object.assign({}, ticketsById[id])
      return ticket
    })
    return {
      tickets: ticketWithoutActions,
      // The customer keys are camelCased so they can be sent as API payloads
      // Also filter out null props
      authors: authors.map(author =>
        mapObjectKeys(camelize)(filter(e => e, author))
      ),
    }
  }
)

// New Ticket Context
export const selectNewEmailContext = createSelector(
  selectNewEmailCacheKey,
  selectReplyDraft,
  (cacheKey, draft) => {
    let customer = getFirstOrEmptyFromDraftRecipient('to', draft)
    if (!draft || isEmptyObject(draft) || !customer || isEmptyObject(customer))
      return null
    const draftWithoutActions = Object.assign({}, draft)
    customer = getFirstOrEmptyFromDraftRecipient('to', draftWithoutActions)
    return {
      // Relying on the draft decorator to populate draft.customer is hairy. We
      // already have all the info we need in draft.to, so just copy that to
      // .customer (which is what the endpoint expects in the payload
      ticket: { ...draftWithoutActions, customer },
    }
  }
)

// New Note/Log Conversation Context
export const selectNewNoteContext = createSelector(
  selectNewEmailCacheKey,
  selectNoteDraft,
  (cacheKey, draft) => {
    let customer = getFirstOrEmptyFromDraftRecipient('to', draft)
    if (!draft || isEmptyObject(draft) || isEmptyObject(customer)) return null
    const draftWithoutActions = Object.assign({}, draft)
    customer = getFirstOrEmptyFromDraftRecipient('to', draft)
    return {
      // Relying on the draft decorator to populate draft.customer is hairy. We
      // already have all the info we need in draft.to, so just copy that to
      // .customer (which is what the endpoint expects in the payload
      ticket: { ...draftWithoutActions, customer },
    }
  }
)

export const selectContextCacheKeyBuilder = createSelector(
  selectCurrentTicket,
  selectIsTicketSelectionMode,
  selectIsCreatingNewEmail,
  selectIsLoggingNewConversation,
  selectTicketCacheKey,
  selectTicketSelectionCacheKey,
  selectNewEmailCacheKey,
  selectNewNoteCacheKey,
  (
    ticket,
    isSelectionMode,
    isNewEmail,
    isNewNote,
    ticketCacheKey,
    ticketSelectionCacheKey,
    newEmailCacheKey,
    newNoteCacheKey
  ) => {
    let cacheKey

    if (isSelectionMode) {
      cacheKey = ticketSelectionCacheKey
    } else if (isNewEmail) {
      cacheKey = newEmailCacheKey
    } else if (isNewNote) {
      cacheKey = newNoteCacheKey
    } else if (ticket && ticket.id) {
      cacheKey = ticketCacheKey
    }

    return memoize(location => {
      if (!cacheKey) return null
      return JSON.stringify({
        ...cacheKey,
        location,
      })
    })
  }
)

export const selectContextBuilder = createSelector(
  selectContextCacheKeyBuilder,
  selectIsTicketSelectionMode,
  selectCurrentTicket,
  selectIsCreatingNewEmail,
  selectIsLoggingNewConversation,
  selectTicketContext,
  selectTicketSelectionContext,
  selectNewEmailContext,
  selectNewNoteContext,
  (
    buildCacheKey,
    isSelectionMode,
    ticket,
    isNewEmail,
    isNewNote,
    ticketContext,
    ticketSelectionContext,
    newEmailContext,
    newNoteContext
  ) => {
    let context = {}
    if (isSelectionMode) {
      context = ticketSelectionContext
    } else if (isNewEmail) {
      context = newEmailContext
    } else if (isNewNote) {
      context = newNoteContext
    } else if (ticket && ticket.id) {
      context = ticketContext
    }

    return memoize(location => {
      return {
        ...context,
        id: buildCacheKey(location),
        location,
      }
    })
  }
)

export const selectCardsSortedByPosition = createSelector(
  selectCards,
  embeddableCards => {
    return embeddableCards.sort((a, b) => a.position - b.position)
  }
)

export const selectFindCardsSortedByPosition = createSelector(
  selectCards,
  embeddableCards => {
    return memoize(location => {
      if (location === locations.PERMANENT) return embeddableCards
      return embeddableCards.filter(card => {
        return card.panels.indexOf(location) > -1
      })
    })
  }
)

export const selectContextId = createSelector(
  selectIsTicketSelectionMode,
  selectCurrentTicketId,
  selectIsCreatingNewEmail,
  selectIsLoggingNewConversation,
  selectSelectedTicketIds,
  selectReplyDraft,
  selectNoteDraft,
  (
    selectionMode,
    ticketId,
    isNewEmail,
    isNewNote,
    ticketIds,
    draft,
    noteDraft
  ) => {
    if (selectionMode) return ticketIds.join(',')
    if (isNewEmail && draft && draft.to)
      return getFirstOrEmptyFromDraftRecipient('to', draft).id
    if (isNewNote && noteDraft && noteDraft.to)
      return getFirstOrEmptyFromDraftRecipient('to', draft).id
    if (!isNewEmail && !isNewNote && ticketId) return ticketId
    return null
  }
)

export const selectCardsWithTab = createSelector(
  selectCardsSortedByPosition,
  cards => cards.filter(c => c.hasTab)
)

export function selectActiveCardTab(state) {
  return state.embeddableCards.activeTab
}
